import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sios_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors — only redirect to admin login if we're already inside the admin area.
// Public pages (e.g. judge voting) get a normal rejected promise so they can show their own UX.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isAdminArea = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
      if (isAdminArea) {
        localStorage.removeItem('sios_token');
        localStorage.removeItem('sios_user');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Editions
export const editionsAPI = {
  getAll: (status) => api.get('/editions', { params: { status } }),
  getOne: (id) => api.get(`/editions/${id}`),
  create: (data) => api.post('/editions', data),
  update: (id, data) => api.put(`/editions/${id}`, data),
  delete: (id) => api.delete(`/editions/${id}`),
};

// Events
export const eventsAPI = {
  getAll: (params) => api.get('/events', { params }),
  getOne: (id, preview) => api.get(`/events/${id}`, { params: preview ? { preview } : {} }),
  getBySlug: (slug, preview) => api.get(`/events/slug/${slug}`, { params: preview ? { preview } : {} }),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  getAgenda: (id) => api.get(`/public/events/${id}/agenda`),
  getSpeakers: (id) => api.get(`/public/events/${id}/speakers`),
};

// Speakers
export const speakersAPI = {
  getAll: () => api.get('/speakers'),
  getPublic: () => api.get('/speakers', { params: { public_only: true } }),
  getHomepage: () => api.get('/speakers/homepage'),
  getOne: (id) => api.get(`/speakers/${id}`),
  getBySlug: (slug) => api.get(`/speakers/slug/${slug}`),
  create: (data) => api.post('/speakers', data),
  update: (id, data) => api.put(`/speakers/${id}`, data),
  delete: (id) => api.delete(`/speakers/${id}`),
};

// Tracks
export const tracksAPI = {
  getAll: () => api.get('/tracks'),
  create: (data) => api.post('/tracks', data),
  update: (id, data) => api.put(`/tracks/${id}`, data),
  delete: (id) => api.delete(`/tracks/${id}`),
};

// Sessions
export const sessionsAPI = {
  getAll: (params) => api.get('/sessions', { params }),
  getOne: (id) => api.get(`/sessions/${id}`),
  create: (data) => api.post('/sessions', data),
  update: (id, data) => api.put(`/sessions/${id}`, data),
  delete: (id) => api.delete(`/sessions/${id}`),
};

// Galleries
export const galleriesAPI = {
  getAll: () => api.get('/galleries'),
  getOne: (id) => api.get(`/galleries/${id}`),
  getByEvent: (eventId) => api.get(`/galleries/event/${eventId}`),
  create: (data) => api.post('/galleries', data),
  update: (id, data) => api.put(`/galleries/${id}`, data),
  delete: (id) => api.delete(`/galleries/${id}`),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Challenges
export const challengesAPI = {
  getAll: (params) => api.get('/challenges', { params }),
  getOne: (id) => api.get(`/challenges/${id}`),
  create: (data) => api.post('/challenges', data),
  update: (id, data) => api.put(`/challenges/${id}`, data),
  delete: (id) => api.delete(`/challenges/${id}`),
  spawn: (parentId, data) => api.post(`/challenges/${parentId}/spawn`, data),
  // Judges
  getJudges: (challengeId) => api.get(`/challenges/${challengeId}/judges`),
  searchJudges: (q, excludeChallengeId) => api.get('/challenges/judges/search', { params: { q, exclude_challenge_id: excludeChallengeId } }),
  addJudge: (challengeId, data) => api.post(`/challenges/${challengeId}/judges`, data),
  updateJudge: (judgeId, data) => api.put(`/challenges/judges/${judgeId}`, data),
  deleteJudge: (judgeId) => api.delete(`/challenges/judges/${judgeId}`),
  // Startups
  getStartups: (challengeId) => api.get(`/challenges/${challengeId}/startups`),
  searchStartups: (q, excludeChallengeId) => api.get('/challenges/startups/search', { params: { q, exclude_challenge_id: excludeChallengeId } }),
  addStartup: (challengeId, data) => api.post(`/challenges/${challengeId}/startups`, data),
  updateStartup: (startupId, data) => api.put(`/challenges/startups/${startupId}`, data),
  deleteStartup: (startupId) => api.delete(`/challenges/startups/${startupId}`),
  importStartupsExcel: (challengeId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/challenges/${challengeId}/startups/import-excel`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Results & Email
  getResults: (challengeId) => api.get(`/challenges/${challengeId}/results`),
  deleteVote: (challengeId, judgeId) => api.delete(`/challenges/${challengeId}/votes/judge/${judgeId}`),
  sendEmails: (challengeId, data) => api.post(`/challenges/${challengeId}/send-emails`, data),
  // Voting (public)
  authJudge: (data) => api.post('/challenges/vote/auth', data),
  castVote: (data) => api.post('/challenges/vote', data),
  // Judge profile
  getJudgeProfile: (identifier) => api.get(`/challenges/judge/profile/${identifier}`),
};

export default api;
